import moment from 'moment';
import { RawLocation } from 'vue-router';
import {
  AvailableService,
  deliveryTimeFormattedType, deliveryTimeType, PackageItem,
} from '@/features/shipment/types';
import store from '@/features/common/store';
import i18n from '@/i18n';
import router from '@/router';
import { UserInfo } from '@/features/users/types';

export const copyTextToClipboard = (copiedText: string): void => {
  navigator.clipboard.writeText(copiedText);
};

export const isLocalhost = (): boolean => window.location.hostname === 'localhost';

export const getDomain = (): string => window.location.hostname.split('.')[0];

export const redirectTo = (url: string, isLocalhostExternal = false): void => {
  let fullUrl = url;

  if (isLocalhost() && isLocalhostExternal) {
    fullUrl = `https://test.secureship.ca${url}`;
  }

  if (isLocalhost() && !isLocalhostExternal) {
    fullUrl = `/ship${url}`;
  }

  window.location.assign(fullUrl);
};

export const isMobileBrowser = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const downloadURI = (uri: string, customName = '', isNewWindow = false): void => {
  const link = document.createElement('a');
  link.setAttribute('href', uri);

  if (customName) {
    link.setAttribute('download', customName);
  }

  if (isNewWindow) {
    link.setAttribute('target', '_blank');
  }

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const thousandSeparators = (num: number | string): string => {
  const [integerPart, fractionalPart] = num.toString().split('.');

  const regexp = /\B(?=(\d{3})+(?!\d))/g;
  const integerWithCommas = integerPart.replace(regexp, ',');

  return fractionalPart ? `${integerWithCommas}.${fractionalPart}` : integerWithCommas;
};

export const getMarkerStyle = (idx: number, colors: Array<string>): string => `background-color: ${colors[idx]};`;

export const getPrice = (val: number, roundTo = 2): string => `$${thousandSeparators(val.toFixed(roundTo))}`;

export const getPriceWithoutRound = (val: number): string => `$${thousandSeparators(val)}`;

export const getDimensions = (data: PackageItem, addUnits = true): string => {
  const dims = `${data.length} x ${data.width} x ${data.height}`;

  if (!addUnits) {
    return dims;
  }

  return `${data.dimUnits ? `${dims} ${data.dimUnits}` : ''}`;
};

export const getInsuranceAmount = (data: PackageItem): string => `${data.insurance ? `<br>$${data.insurance} insurance` : ''}`;

export const getPackageDescription = (data: PackageItem): string => `${data.description ? `<br>Description: ${data.description}` : ''}`;

export const getPackageDetails = (data: PackageItem, trackingNumber: string): string => {
  const output = [
    data.packageType,
    `${data.weight} ${data.weightUnits}`,
    getDimensions(data),
    getInsuranceAmount(data),
    `${data.isAdditionalHandling ? `<br>${i18n.t('shipment.requiresAdditionalHandling')}` : ''}`,
    getPackageDescription(data),
  ];

  if (trackingNumber) {
    output.push(`#${trackingNumber}`);
  }

  return output.filter(Boolean).join(', ');
};

export const scrollToInPage = (xPosition = 0, yPosition = 1, isSmooth = false): void => {
  if (isSmooth) {
    document.documentElement.style.scrollBehavior = 'smooth';
  }
  window.scrollTo(xPosition, yPosition);
  if (isSmooth) {
    document.documentElement.style.scrollBehavior = '';
  }
};

export const scrollToBottom = (isSmooth = true): void => {
  scrollToInPage(0, document.body.scrollHeight, isSmooth);
};

export const scrollToElementById = (elementId: string): void => {
  const element = document.getElementById(elementId);

  if (element) {
    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const elementMiddle = rect.top + scrollTop - (window.innerHeight / 2) + (rect.height / 2);
    scrollToInPage(0, elementMiddle, true);
  }
};

export const scrollByInPage = (xPosition = 0, yPosition = 1, isSmooth = false): void => {
  if (isSmooth) {
    document.documentElement.style.scrollBehavior = 'smooth';
  }
  window.scrollBy(xPosition, yPosition);
  if (isSmooth) {
    document.documentElement.style.scrollBehavior = '';
  }
};

export const useFormatTimestamp = (dateString: string, customText = ''): string => {
  const mDate = moment(dateString, 'YYYY-MM-DD');
  if (mDate.year() <= 1) return customText;
  return mDate.format('MMMM D, YYYY');
};

export const getDeliveryDateAndTime = (
  deliveryTime: deliveryTimeType,
): deliveryTimeFormattedType => {
  if (deliveryTime
&& deliveryTime.friendlyTime
&& deliveryTime.friendlyTime.toLowerCase() !== 'Delivery Date Unavailable'.toLowerCase()
  ) {
    const isReadyTimeIncluded = deliveryTime.friendlyTime.toLowerCase().includes(
      'Ready Time'.toLowerCase(),
    );

    if (isReadyTimeIncluded) {
      return {
        date: deliveryTime.friendlyTime,
        time: '',
        isTag: true,
      };
    }

    const dateTimeStrArr = deliveryTime.friendlyTime.split(' by ');
    return {
      date: dateTimeStrArr[0],
      time: `by ${dateTimeStrArr[1] || i18n.t('labels.endOfDay')}`,
      isTag: true,
    };
  }

  return {
    date: i18n.t('labels.transitTimeNotAvailable') as string,
    time: '',
    isTag: false,
  };
};

export const formatFastestDeliveryTime = (
  deliveryTime: deliveryTimeType,
): { date: string; time: string } | null => {
  const { date, time, isTag } = getDeliveryDateAndTime(deliveryTime);

  if (!isTag) return null;

  const isReadyTimeIncluded = deliveryTime.friendlyTime
    .toLowerCase()
    .includes('ready time');

  if (isReadyTimeIncluded) {
    let cleanedTime = deliveryTime.friendlyTime
      .replace(/Ready Time/i, '')
      .trim();

    cleanedTime = cleanedTime.replace(/^([+-])/, '').trim();

    return {
      date: '',
      time: cleanedTime,
    };
  }

  const dateParts = date.split(' ');
  if (dateParts.length >= 3) {
    return {
      date: `${dateParts[1]} ${dateParts[2]}`,
      time,
    };
  }

  return {
    date,
    time,
  };
};

export function isValidDeliveryTime(service: AvailableService): boolean {
  const { deliveryTime } = service;
  const friendly = deliveryTime?.friendlyTime?.toLowerCase();
  const isUnavailable = !friendly || friendly === 'delivery date unavailable';
  const isInvalidTime = !deliveryTime?.time || deliveryTime.time === '0001-01-01T00:00:00';
  return !isUnavailable && !isInvalidTime;
}

export function getCheapestService(services: AvailableService[]): AvailableService | undefined {
  const validServices = (services ?? []).filter(
    (s) => typeof s.total === 'number' && !Number.isNaN(s.total),
  );

  if (!validServices.length) return undefined;

  return validServices.reduce((min, s) => {
    // If prices are different, return the cheaper one
    if (s.total < min.total) return s;
    if (s.total > min.total) return min;

    // Tiebreaker: If prices are equal, compare delivery times
    // Only compare if both have valid delivery times
    const sHasValidTime = isValidDeliveryTime(s);
    const minHasValidTime = isValidDeliveryTime(min);

    if (sHasValidTime && minHasValidTime) {
      try {
        const sTime = Date.parse(s.deliveryTime.time);
        const minTime = Date.parse(min.deliveryTime.time);

        if (!Number.isNaN(sTime) && !Number.isNaN(minTime)) {
          return sTime < minTime ? s : min;
        }
      } catch {
        // If delivery time comparison fails, return current min
      }
    }

    // If only one has valid delivery time, prefer that one
    if (sHasValidTime && !minHasValidTime) return s;
    if (!sHasValidTime && minHasValidTime) return min;

    return min;
  }, validServices[0]);
}

export function getEarlierService(
  earliest: AvailableService,
  current: AvailableService,
): AvailableService {
  try {
    const currentTime = Date.parse(current.deliveryTime.time);
    const earliestTime = Date.parse(earliest.deliveryTime.time);
    if (Number.isNaN(earliestTime)) return current;
    if (Number.isNaN(currentTime)) return earliest;

    // If delivery times are different, return the earlier one
    if (currentTime < earliestTime) return current;
    if (currentTime > earliestTime) return earliest;

    // Tiebreaker: If delivery times are equal, compare prices
    const currentPrice = current.total;
    const earliestPrice = earliest.total;

    if (typeof currentPrice === 'number' && typeof earliestPrice === 'number'
        && !Number.isNaN(currentPrice) && !Number.isNaN(earliestPrice)) {
      return currentPrice < earliestPrice ? current : earliest;
    }

    return earliest;
  } catch {
    return earliest;
  }
}

export function getFastestService(services: AvailableService[]): AvailableService | undefined {
  const validServices = (services ?? []).filter(isValidDeliveryTime);
  if (!validServices.length) return undefined;

  return validServices.reduce(getEarlierService);
}

export function compareDeliveryTimeTiebreaker(
  a: AvailableService,
  b: AvailableService,
): number {
  const aHasValidTime = isValidDeliveryTime(a);
  const bHasValidTime = isValidDeliveryTime(b);

  if (aHasValidTime && bHasValidTime) {
    try {
      const aTime = Date.parse(a.deliveryTime.time);
      const bTime = Date.parse(b.deliveryTime.time);

      if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
        return aTime - bTime;
      }
    } catch {
      return 0;
    }
  }

  if (aHasValidTime && !bHasValidTime) return -1;
  if (!aHasValidTime && bHasValidTime) return 1;

  return 0;
}

export function getValidPrice(service: AvailableService): number {
  return typeof service.total === 'number' && !Number.isNaN(service.total)
    ? service.total
    : Infinity;
}

export const useConvertTime = (time: string | undefined, returnValue = ''): string => {
  if (!time) return returnValue;

  try {
    return moment(time).format('h:mm A');
  } catch (err) {
    return '';
  }
};

export const formatAMPM = (date: Date): string | null => {
  try {
    let hours = date.getHours();
    let minutes: number | string = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours %= 12;
    hours = hours || 12;
    minutes = minutes < 10 ? `0${minutes}` : minutes;

    return `${hours}:${minutes} ${ampm}`;
  } catch (err) {
    return null;
  }
};

export const getISOWithCurrentTime = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();

  date.setHours(
    now.getHours(),
    now.getMinutes(),
    now.getSeconds(),
    now.getMilliseconds(),
  );

  return date.toISOString();
};

export const isStringContains = (str: string, searchTerm: string): boolean => {
  return str.toUpperCase().indexOf(searchTerm) > -1;
};

export const truncateText = (text: string, charLimit: number): string => {
  return (text.length > charLimit)
    ? `${text.substring(0, charLimit - 1)}...`
    : text;
};

export const showErrorAlertDialog = (
  errorMessage = i18n.t('alertDialog.somethingWentWrong'),
): void => {
  store.commit('addAlertDialog', {
    type: 'error',
    bodyText: errorMessage,
  });
};

export const showSuccessAlertDialog = (bodyText: string): void => {
  store.commit('addAlertDialog', {
    type: 'success',
    bodyText,
  });
};

export const showWarningAlertDialog = (bodyText: string): void => {
  store.commit('addAlertDialog', {
    type: 'warning',
    bodyText,
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deepClone = (obj: { [key: string]: any }): any => {
  return JSON.parse(JSON.stringify(obj));
};

export const getRandomIntInclusive = (minArg: number, maxArg: number): number => {
  const min = Math.ceil(minArg);
  const max = Math.floor(maxArg);
  return Math.floor(Math.random() * (max - min + 1) + min);
};

export const getOrigin = (): string => {
  const { origin } = window.location;

  return `${origin}${process.env.VUE_APP_ORIGIN}`;
};

export const getFullUrlWithOrigin = (url: string): string => {
  return `${getOrigin()}${url}`;
};

export const fetchMoreUsingScrollWindow = (callback: () => void): void => {
  if (Math.abs(window.innerHeight + window.scrollY + 1) >= document.body.offsetHeight) {
    callback();
  }
};

export const redirectToSignInWithLogoutCheck = (link: string): void => {
  const isLogoutIncludes = link.includes('logout') || link.includes('sign-out');
  const encodedUrl = !isLogoutIncludes
    ? encodeURIComponent(isLocalhost() ? link : `/ship${link}`)
    : '';
  redirectTo(encodedUrl ? `/sign-in?redirectTo=${encodedUrl}` : '/sign-in');
};

export const redirectToSignIn = (): void => {
  const redirectToLink: string | null = window.location.href.replaceAll(getOrigin(), '');

  if (redirectToLink.includes('/sign-in')) return;

  if (isLocalhost()) {
    const route: RawLocation = { name: 'sign-in' };

    if (!redirectToLink.includes('logout')) {
      route.query = { redirectToLink };
    }

    router.push(route);
    return;
  }

  redirectToSignInWithLogoutCheck(redirectToLink);
};

export const isEqual = (obj1: unknown, obj2: unknown): boolean => {
  if (!obj1 || !obj2) return false;

  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

export const parseFirstAndLastName = (fullName: string): { firstName: string, lastName: string} => {
  const fullNameArr = fullName.trim().split(' ');
  let firstName = '';
  let lastName = '';

  if (fullNameArr.length > 1) {
    firstName = fullNameArr.slice(0, fullNameArr.length - 1).join(' ');
    lastName = fullNameArr[fullNameArr.length - 1];
  } else {
    [firstName] = fullNameArr;
    lastName = '';
  }

  return { firstName, lastName };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function replaceEmptyStringWithNull(obj: unknown): any {
  if (obj === '') return null;

  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => replaceEmptyStringWithNull(item));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      result[key] = value === '' ? null : replaceEmptyStringWithNull(value);
    }
  }

  return result;
}

export function removeQueryParams(url: string, paramNames: string[]): string {
  const urlObj = new URL(url);
  const { searchParams } = urlObj;
  for (const paramName of paramNames) {
    searchParams.delete(paramName);
  }
  urlObj.search = searchParams.toString();
  return urlObj.toString();
}

export function removeAllQueryParams(paramNames: string[]): void {
  const newUrl = removeQueryParams(window.location.href, paramNames);
  window.history.replaceState(null, '', newUrl);
}

export function getProfileInitials(firstName: string): string {
  return firstName ? firstName.charAt(0) : '';
}

export function hideAnimation(componentId: string, param: string, delay = 400): void {
  if (typeof document === 'undefined' || !document) return;
  const animation = document.getElementById(componentId);
  if (!animation) return;

  setTimeout(() => {
    animation.classList.add('hide-slide-animation');

    setTimeout(() => {
      animation.style.display = 'none';
      removeAllQueryParams([param]);
    }, 500);
  }, delay);
}

export const normalizeString = (value: string | undefined): string => {
  return value ? value?.trim() : '';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const compareFields = <T extends Record<string, any>>
  (obj1: T, obj2: T, fields: (keyof T)[]): boolean => {
  if (!obj1 || !obj2) return false;

  return fields.every((field) => {
    const value1 = obj1[field];
    const value2 = obj2[field];

    if (typeof value1 === 'string' || typeof value2 === 'string') {
      return normalizeString(value1 as string) === normalizeString(value2 as string);
    }

    if (typeof value1 !== typeof value2) {
      return false;
    }

    return value1 === value2;
  });
};

export function shouldShowBackgroundGradient(userInfo: UserInfo): boolean {
  try {
    const mode = process.env && process.env.VUE_APP_SERVER_MODE;
    const isLiveMode = mode === 'live';
    const threshold = isLiveMode ? 153185 : 331686;

    const account = userInfo && userInfo.account;
    if (!account) return false;

    const accountNum = Number(account);
    return accountNum > threshold && accountNum < 999999;
  } catch (e) {
    return false;
  }
}
